import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import NewsletterForm from "@/components/NewsletterForm";
import CTAButton from "@/components/CTAButton";
import { Reveal } from "@/components/motion";
import { getArticle, ARTICLES } from "@/lib/articles";
import { getHandbookArticle, HANDBOOK } from "@/lib/handbook";
import { track } from "@/lib/analytics";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

function Block({ block }) {
  switch (block.type) {
    case "h2":
      return <h2 className="pt-4 font-display text-2xl font-semibold tracking-tight text-foreground">{block.text}</h2>;
    case "quote":
      return (
        <blockquote className="border-l-2 border-brand-teal pl-5 font-display text-xl italic leading-snug text-foreground">
          {block.text}
        </blockquote>
      );
    case "ul":
      return (
        <ul className="space-y-2.5">
          {block.items.map((li) => (
            <li key={li} className="flex items-start gap-2.5">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-teal" />
              <span>{li}</span>
            </li>
          ))}
        </ul>
      );
    default:
      return <p>{block.text}</p>;
  }
}

export default function Article() {
  const { slug } = useParams();
  const article = getArticle(slug) || getHandbookArticle(slug);

  useEffect(() => {
    if (article) track("article_view", { slug, category: article.category });
  }, [slug, article]);

  if (!article) {
    return (
      <>
        <Seo title="Article not found" path={`/resources/${slug}`} />
        <PageHeader eyebrow="Resources" title="Article not found" subtitle="This article doesn't exist or has moved." />
        <div className="mx-auto max-w-3xl px-5 py-16 sm:px-8">
          <CTAButton to="/resources" variant="gradient" size="md">Back to Resources</CTAButton>
        </div>
      </>
    );
  }

  const related = [...ARTICLES, ...HANDBOOK].filter((a) => a.slug !== slug).slice(0, 2);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    articleSection: article.category,
    datePublished: article.date,
    publisher: { "@type": "Organization", name: "StUpEco Workspace" },
  };

  return (
    <>
      <Seo title={article.title} description={article.excerpt} path={`/resources/${slug}`} jsonLd={jsonLd} />
      <PageHeader eyebrow={article.category} title={article.title} subtitle={article.excerpt}>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span>{article.read}</span>
          <span className="h-1 w-1 rounded-full bg-border" />
          <span>{article.date}</span>
        </div>
      </PageHeader>

      <article className="border-b border-border py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal className="overflow-hidden rounded-3xl border border-border">
            <img src={article.hero} alt={article.title} loading="lazy" className="h-64 w-full object-cover sm:h-80" />
          </Reveal>

          <Reveal className="mt-10 space-y-5 text-base leading-relaxed text-muted-foreground" data-testid="article-body">
            {article.content.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </Reveal>

          <Reveal className="mt-12 rounded-3xl border border-border bg-card p-8">
            <h3 className="font-display text-lg font-semibold tracking-tight">Enjoying the series?</h3>
            <p className="mt-1 text-sm text-muted-foreground">Get the next article in your inbox.</p>
            <div className="mt-4 max-w-sm">
              <NewsletterForm source={`/resources/${slug}`} compact />
            </div>
          </Reveal>

          {related.length > 0 && (
            <div className="mt-12">
              <p className="eyebrow mb-4 text-muted-foreground">Keep reading</p>
              <div className="grid gap-4 sm:grid-cols-2">
                {related.map((a) => (
                  <Link key={a.slug} to={`/resources/${a.slug}`} className="card-hover group rounded-2xl border border-border bg-card p-5 hover:border-foreground/20">
                    <span className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold text-muted-foreground ring-1 ring-border">{a.category}</span>
                    <h4 className="mt-3 font-display text-base font-semibold tracking-tight">{a.title}</h4>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
                      Read <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-10">
            <Link to="/resources" data-testid="back-to-resources" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
              <ArrowLeft className="h-4 w-4" /> Back to all articles
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}
